import { useEffect, useRef, useState } from "react";
import { Send, Mic, Square, Video, Image as ImageIcon, Play, Pause } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import VideoRecorder from "./VideoRecorder";

function AudioBubble({ src }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  return (
    <div className="flex items-center gap-3 min-w-[180px]">
      <button onClick={() => { const a = ref.current; if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); } }}
        className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>
      <div className="flex-1 flex items-center gap-0.5 h-8">
        {[...Array(18)].map((_, i) => <div key={i} className="flex-1 bg-white/40 rounded-full" style={{ height: `${20 + Math.abs(Math.sin(i)) * 60}%` }} />)}
      </div>
      <audio ref={ref} src={src} onEnded={() => setPlaying(false)} className="hidden" />
    </div>
  );
}

export default function MessageThread({ conversationId, headerName, headerPic }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const [recTime, setRecTime] = useState(0);
  const [showVideoRec, setShowVideoRec] = useState(false);
  const recorder = useRef(null);
  const chunks = useRef([]);
  const timerRef = useRef(null);
  const bottomRef = useRef(null);
  const videoInput = useRef(null);
  const imageInput = useRef(null);

  const load = async () => {
    if (!conversationId) return;
    const { data } = await api.get(`/conversations/${conversationId}/messages`);
    setMessages(data.messages);
  };

  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [conversationId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendMessage = async (payload) => {
    const { data } = await api.post(`/conversations/${conversationId}/messages`, payload);
    setMessages((m) => [...m, data]);
  };

  const sendText = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text; setText("");
    await sendMessage({ type: "text", text: t });
  };

  const toBase64 = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
  const uploadMedia = async (b64, kind) => { const { data } = await api.post("/upload/media", { data: b64, kind }); return data.url; };

  const onFile = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { alert("File too large (max 8MB for demo)."); return; }
    try {
      const b64 = await toBase64(file);
      const url = await uploadMedia(b64, type);
      await sendMessage({ type, media: url, text: "" });
    } catch (err) { alert("Upload failed, please try again."); }
    e.target.value = "";
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      mr.ondataavailable = (ev) => chunks.current.push(ev.data);
      mr.onstop = async () => {
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        const b64 = await toBase64(blob);
        try { const url = await uploadMedia(b64, "audio"); await sendMessage({ type: "audio", media: url, text: "" }); } catch (err) { alert("Upload failed."); }
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      recorder.current = mr;
      setRecording(true);
      setRecTime(0);
      timerRef.current = setInterval(() => setRecTime((t) => t + 1), 1000);
    } catch (e) {
      alert("Microphone access denied.");
    }
  };

  const stopRec = () => {
    recorder.current?.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  return (
    <div className="flex flex-col h-full">
      {headerName && (
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border card-surface">
          <div className="w-9 h-9 rounded-full bg-brand/20 flex items-center justify-center overflow-hidden">
            {headerPic ? <img src={headerPic} alt="" className="w-full h-full object-cover" /> : <span className="font-heading text-brand">{headerName[0]}</span>}
          </div>
          <span className="font-semibold">{headerName}</span>
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} data-testid="chat-message" className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-brand text-white rounded-br-sm" : "card-surface rounded-bl-sm"}`}>
                {m.type === "text" && <p className="text-sm break-words">{m.text}</p>}
                {m.type === "audio" && <AudioBubble src={m.media} />}
                {m.type === "video" && <video src={m.media} controls className="rounded-lg max-w-[240px]" />}
                {m.type === "image" && <img src={m.media} alt="" className="rounded-lg max-w-[200px]" />}
                <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-tertiary"}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={sendText} className="border-t border-border p-3 flex items-center gap-2 card-surface">
        {recording ? (
          <div className="flex-1 flex items-center gap-3">
            <span className="w-3 h-3 rounded-full bg-brand animate-pulse" />
            <span className="font-num text-brand">{String(Math.floor(recTime / 60)).padStart(2, "0")}:{String(recTime % 60).padStart(2, "0")}</span>
            <span className="text-secondary text-sm">Recording...</span>
            <button type="button" data-testid="chat-stop-rec" onClick={stopRec} className="ml-auto w-10 h-10 rounded-full bg-brand flex items-center justify-center">
              <Square className="w-4 h-4 text-white" />
            </button>
          </div>
        ) : (
          <>
            <button type="button" data-testid="chat-image" onClick={() => imageInput.current.click()} className="text-secondary hover:text-white p-2"><ImageIcon className="w-5 h-5" /></button>
            <button type="button" data-testid="chat-video" onClick={() => setShowVideoRec(true)} className="text-secondary hover:text-white p-2"><Video className="w-5 h-5" /></button>
            <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Message your coach..."
              className="flex-1 bg-surface-elevated border border-border rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
            {text.trim() ? (
              <button type="submit" data-testid="chat-send" className="w-10 h-10 rounded-full bg-brand flex items-center justify-center"><Send className="w-4 h-4 text-white" /></button>
            ) : (
              <button type="button" data-testid="chat-rec" onClick={startRec} className="w-10 h-10 rounded-full bg-brand flex items-center justify-center"><Mic className="w-4 h-4 text-white" /></button>
            )}
          </>
        )}
        <input ref={videoInput} type="file" accept="video/*" capture="user" className="hidden" onChange={(e) => onFile(e, "video")} />
        <input ref={imageInput} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e, "image")} />
      </form>
      {showVideoRec && (
        <VideoRecorder label="Record Video Message"
          onComplete={async (b64) => { setShowVideoRec(false); try { const url = await uploadMedia(b64, "video"); await sendMessage({ type: "video", media: url, text: "" }); } catch (err) { alert("Upload failed, please try again."); } }}
          onClose={() => setShowVideoRec(false)} />
      )}
    </div>
  );
}
